import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone, Share, Plus } from 'lucide-react';

const DISMISSED_KEY = 'tt_install_dismissed_at';
const DISMISS_DAYS = 14;

/**
 * Smart install prompt that handles all platforms:
 * - Android/Desktop Chrome/Edge: Native install button (uses beforeinstallprompt event)
 * - iOS Safari: Custom instruction modal (Apple doesn't allow programmatic install)
 * - Already installed: Hides itself
 * - Dismissed recently: Hidden for 14 days
 *
 * Usage:
 *   <InstallAppPrompt theme={theme} variant="banner" />        // small banner in app
 *   <InstallAppPrompt theme={theme} variant="hero" />          // big CTA on marketing
 */
export function InstallAppPrompt({ theme, variant = 'banner' }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosModal, setShowIosModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Check if running as installed PWA (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    // Check if recently dismissed
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) {
        setDismissed(true);
        return;
      }
    }

    // Listen for the install prompt event (Android/Desktop)
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    // Listen for successful install
    const onInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Hide if already installed or dismissed
  if (installed || dismissed) return null;

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const canInstallNatively = installPrompt !== null;

  // Don't show anything if neither native install nor iOS
  // (e.g. desktop Firefox doesn't support PWA installation)
  if (!canInstallNatively && !isIos) return null;

  async function handleInstall() {
    if (canInstallNatively) {
      // Native install — works on Android/Desktop Chrome/Edge
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
      } else {
        // User dismissed — remember it
        handleDismiss();
      }
      setInstallPrompt(null);
    } else if (isIos) {
      // Show iOS instructions modal
      setShowIosModal(true);
    }
  }

  function handleDismiss() {
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    setDismissed(true);
  }

  if (variant === 'hero') {
    return (
      <>
        <button onClick={handleInstall} style={{
          padding: '14px 24px',
          background: theme.gold,
          color: '#1A1917',
          border: 'none',
          borderRadius: 12,
          fontSize: 15,
          fontWeight: 700,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <Download size={16} /> Installér app
        </button>

        {showIosModal && (
          <IosInstallModal theme={theme} onClose={() => setShowIosModal(false)} />
        )}
      </>
    );
  }

  // Default: banner variant
  return (
    <>
      <div style={{
        background: `linear-gradient(135deg, ${theme.coral}15, ${theme.gold}10)`,
        border: `1px solid ${theme.coral}40`,
        borderRadius: 12,
        padding: '12px 14px',
        marginBottom: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <div style={{
          width: 36, height: 36,
          borderRadius: 10,
          background: theme.coral,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Smartphone size={18} color="white" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.text, marginBottom: 2 }}>
            Installér Tag Team
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.4 }}>
            Få appen på din startskærm — virker som en rigtig app
          </div>
        </div>

        <button onClick={handleInstall} style={{
          padding: '8px 14px',
          background: theme.coral,
          color: 'white',
          border: 'none',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          flexShrink: 0,
        }}>
          Installér
        </button>

        <button onClick={handleDismiss} style={{
          background: 'transparent',
          border: 'none',
          color: theme.textMuted,
          cursor: 'pointer',
          padding: 4,
          flexShrink: 0,
        }} aria-label="Skjul">
          <X size={16} />
        </button>
      </div>

      {showIosModal && (
        <IosInstallModal theme={theme} onClose={() => setShowIosModal(false)} />
      )}
    </>
  );
}

function IosInstallModal({ theme, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      zIndex: 500,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: theme.bgElevated,
        borderRadius: '20px 20px 0 0',
        padding: '28px 24px 32px',
        maxWidth: 480,
        width: '100%',
        animation: 'tt-slide-up 0.3s ease-out',
      }} onClick={e => e.stopPropagation()}>
        <style>{`@keyframes tt-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56,
            borderRadius: 14,
            background: theme.coral,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <Smartphone size={28} color="white" />
          </div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: theme.text }}>
            Installér Tag Team
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>
            Få appen på din startskærm i 3 trin:
          </p>
        </div>

        <ol style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}>
          <Step number="1" theme={theme}>
            Tryk på <strong>Del</strong>-ikonet
            <Share size={16} color={theme.coral} style={{ verticalAlign: 'middle', marginLeft: 4 }} />
            <span style={{ color: theme.textMuted, fontSize: 12 }}> nederst i Safari</span>
          </Step>
          <Step number="2" theme={theme}>
            Scroll ned og vælg <strong>"Føj til hjemmeskærm"</strong>
            <Plus size={16} color={theme.coral} style={{ verticalAlign: 'middle', marginLeft: 4 }} />
          </Step>
          <Step number="3" theme={theme}>
            Tryk <strong>"Tilføj"</strong> i øverste højre hjørne
          </Step>
        </ol>

        <button onClick={onClose} style={{
          width: '100%',
          marginTop: 20,
          padding: '12px',
          background: theme.bgCard,
          color: theme.text,
          border: `1px solid ${theme.border}`,
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}>
          OK, det forstod jeg
        </button>
      </div>
    </div>
  );
}

function Step({ number, theme, children }) {
  return (
    <li style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '12px 14px',
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderRadius: 10,
    }}>
      <div style={{
        width: 24, height: 24,
        borderRadius: '50%',
        background: theme.coral,
        color: 'white',
        fontSize: 12,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>{number}</div>
      <div style={{ fontSize: 13, color: theme.text, lineHeight: 1.5 }}>{children}</div>
    </li>
  );
}
